import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavContext } from "../context/nav_context";
import { useParams } from "react-router-dom";
import {
  api_v3_user_detail_update,
  api_v3_player_stats,
  frontend_server,
} from "../utils/constants";
import axios from "axios";
import { Loading, TournamentHistory } from "../components";
import { PlayerProfileCard } from "../components/match";
import { usePlayerData } from "../hooks/usePlayerData";
import { useTournamentData } from "../hooks/useTournamentData";
import { useMatchData } from "../hooks/useMatchData";
import { useClansData } from "../hooks/useClansData";
import TournamentSelect from "../components/profile/TournamentSelect";
import MatchList from "../components/profile/MatchList";
import EditProfileSection from "../components/profile/EditProfileSection";
import UsernameEditModal from "../components/profile/UsernameEditModal";
import ClanSelectorModal from "../components/profile/ClanSelectorModal";
import ComunidadSelectorModal from "../components/profile/ComunidadSelectorModal";
import PlayerStats from "../components/profile/PlayerStats";
import { comunidades } from "../utils/constants";

const Profile = () => {
  const { playerId } = useParams();
  const { user, accessToken, refreshUser } = useNavContext();
  const access_token = accessToken || localStorage.getItem("access_token");

  const [isPublicProfile, setIsPublicProfile] = useState(false);
  const [editUsername, setEditUsername] = useState(false);
  const [username, setUsername] = useState("");
  const [showClanSelector, setShowClanSelector] = useState(false);
  const [showComunidadSelector, setShowComunidadSelector] = useState(false);
  const [showAvatarEdit, setShowAvatarEdit] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [selectedClanId, setSelectedClanId] = useState(-1);
  const [comunidadAutonoma, setComunidadAutonoma] = useState("");

  const [playerStats, setPlayerStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const {
    loading: loadingPlayer,
    error: errorPlayer,
    playerData,
    fetchPlayer,
    setPlayerData,
  } = usePlayerData();

  const {
    tournaments,
    selectedTournamentId,
    setSelectedTournamentId,
  } = useTournamentData();

  const { loading: loadingClans, error: errorClans, clans } = useClansData();

  const {
    error: errorMatches,
    matchesByTournament,
    fetchMatchesByTournament,
  } = useMatchData();

  useEffect(() => {
    if (playerId) {
      fetchPlayer(playerId);
      setIsPublicProfile(true);
    } else if (user) {
      setPlayerData(typeof user === "string" ? JSON.parse(user) : user);
      setIsPublicProfile(false);
    }
  }, [playerId, user, fetchPlayer, setPlayerData]);

  useEffect(() => {
    if (playerData) {
      setUsername(playerData.username || "");
      setAvatarUrl(playerData.discordAvatarURL || "");
      setSelectedClanId(playerData.clanId || -1);
      setComunidadAutonoma(playerData.comunidadAutonoma || "");
    }
  }, [playerData]);

  useEffect(() => {
    if (playerData && selectedTournamentId) {
      fetchMatchesByTournament(playerData.playerId, selectedTournamentId);
    }
  }, [selectedTournamentId, playerData, fetchMatchesByTournament]);

  useEffect(() => {
    if (!playerData) return;
    const playerId = playerData.playerId;
    setLoadingStats(true);
    axios.get(api_v3_player_stats(playerId))
      .then((res) => setPlayerStats(res.data))
      .catch(() => {})
      .finally(() => setLoadingStats(false));
  }, [playerData]);

  const handleUsernameChange = (event) => {
    setUsername(event.target.value);
  };

  const handleLogoutClick = () => {
    localStorage.clear();
    window.location.href = frontend_server;
  };

  const handleCloseUsernameModal = () => {
    setEditUsername(false);
    setUsername(playerData?.username || "");
  };

  const handleTournamentChange = (event) => {
    setSelectedTournamentId(event.target.value);
  };

  const saveUsername = async () => {
    if (!access_token) {
      console.error("Es necesario iniciar sesión para guardar el nombre de usuario");
      return;
    }
    const config = { headers: { Authorization: `Bearer ${access_token}` } };
    const data = { ...playerData, username };
    try {
      const response = await axios.post(api_v3_user_detail_update(), data, config);
      if (response.status === 200) {
        await refreshUser();
        setEditUsername(false);
      }
    } catch (error) {
      console.error("Error al guardar el nombre de usuario:", error);
    }
  };

  const saveAvatar = async () => {
    if (!access_token) {
      console.error("Es necesario iniciar sesión para guardar el avatar");
      return;
    }
    const config = { headers: { Authorization: `Bearer ${access_token}` } };
    const data = { ...playerData, discordAvatarURL: avatarUrl };
    try {
      const response = await axios.post(api_v3_user_detail_update(), data, config);
      if (response.status === 200) {
        await refreshUser();
        setShowAvatarEdit(false);
      }
    } catch (error) {
      console.error("Error al guardar el avatar:", error);
    }
  };

  const saveClan = async () => {
    if (!access_token) {
      console.error("Es necesario iniciar sesión para guardar el clan");
      return;
    }
    const config = { headers: { Authorization: `Bearer ${access_token}` } };
    const data = { ...playerData, clanId: selectedClanId };
    try {
      const response = await axios.post(api_v3_user_detail_update(), data, config);
      if (response.status === 200) {
        await refreshUser();
        setShowClanSelector(false);
      }
    } catch (error) {
      console.error("Error al guardar el clan:", error);
    }
  };

  const saveComunidad = async () => {
    if (!access_token) {
      console.error("Es necesario iniciar sesión para guardar la comunidad");
      return;
    }
    const config = { headers: { Authorization: `Bearer ${access_token}` } };
    const data = { ...playerData, comunidadAutonoma };
    try {
      const response = await axios.post(api_v3_user_detail_update(), data, config);
      if (response.status === 200) {
        await refreshUser();
        setShowComunidadSelector(false);
      }
    } catch (error) {
      console.error("Error al guardar la comunidad:", error);
    }
  };

  if (loadingPlayer) {
    return (
      <Canvas>
        <Loading />
      </Canvas>
    );
  }

  if (!playerData && !isPublicProfile) {
    return (
      <Canvas>
        <EmptyState>Debes iniciar sesión para acceder a este perfil.</EmptyState>
      </Canvas>
    );
  }

  if (!playerData || errorPlayer) {
    return (
      <Canvas>
        <EmptyState>Ha ocurrido un error cargando los datos del usuario.</EmptyState>
      </Canvas>
    );
  }

  const matches = selectedTournamentId
    ? matchesByTournament[selectedTournamentId]
    : null;

  const playoffs = matches
    ? matches.filter((m) => m.phase === "PLAYOFF")
    : [];

  const league = matches
    ? matches.filter((m) => m.phase === "REGULAR_LEAGUE")
    : [];

  return (
    <Canvas>
      <UsernameEditModal
        isOpen={editUsername}
        onClose={handleCloseUsernameModal}
        username={username}
        onChange={handleUsernameChange}
        onSave={saveUsername}
      />

      <ClanSelectorModal
        isOpen={showClanSelector}
        onClose={() => setShowClanSelector(false)}
        clans={clans}
        selectedClanId={selectedClanId}
        onSelectClan={setSelectedClanId}
        onSave={saveClan}
        loading={loadingClans}
        error={errorClans}
      />

      <ComunidadSelectorModal
        isOpen={showComunidadSelector}
        onClose={() => setShowComunidadSelector(false)}
        comunidades={comunidades}
        selectedComunidadId={comunidadAutonoma}
        onSelectComunidad={setComunidadAutonoma}
        onSave={saveComunidad}
      />

      {showAvatarEdit && (
        <AvatarModalOverlay onClick={() => setShowAvatarEdit(false)}>
          <AvatarModalBody onClick={(e) => e.stopPropagation()}>
            <AvatarModalTitle>EDITAR AVATAR</AvatarModalTitle>
            <AvatarInput
              placeholder="URL del avatar"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
            />
            <AvatarActions>
              <AvatarCancelBtn onClick={() => setShowAvatarEdit(false)}>
                CANCELAR
              </AvatarCancelBtn>
              <AvatarSaveBtn onClick={saveAvatar}>
                GUARDAR
              </AvatarSaveBtn>
            </AvatarActions>
          </AvatarModalBody>
        </AvatarModalOverlay>
      )}

      <Content>
        <Section>
          <SectionBody>
            <PlayerProfileCard
              playerId={playerData.playerId}
              tournamentId={selectedTournamentId}
              player={{
                ...playerData,
                username,
                clanId: isPublicProfile ? playerData.clanId : selectedClanId,
                comunidadAutonoma: isPublicProfile ? playerData.comunidadAutonoma : comunidadAutonoma,
              }}
              loadingPlayer={false}
              errorPlayer={false}
            />
          </SectionBody>
          {!isPublicProfile && (
            <AvatarEditHint onClick={() => setShowAvatarEdit(true)}>
              CAMBIAR AVATAR
            </AvatarEditHint>
          )}
        </Section>

        <Section>
          <SectionHeader>ESTADÍSTICAS</SectionHeader>
          <PlayerStats stats={playerStats} loading={loadingStats} />
        </Section>

        {!isPublicProfile && (
          <Section>
            <SectionHeader>AJUSTES</SectionHeader>
            <EditProfileSection
              onEditUsername={() => setEditUsername(true)}
              onEditClan={() => setShowClanSelector(true)}
              onEditComunidad={() => setShowComunidadSelector(true)}
              onLogout={handleLogoutClick}
            />
          </Section>
        )}

        <Section>
          <SectionHeader>HISTÓRICO TORNEOS</SectionHeader>
          <TournamentHistory
            tournamentHistory={playerData.tournamentHistory}
            tournaments={tournaments}
          />
        </Section>

        <Section>
          <SectionHeader>PARTIDAS</SectionHeader>
          <TournamentSelect
            tournaments={tournaments}
            selectedId={selectedTournamentId}
            onChange={handleTournamentChange}
          />
          {matches ? (
            <>
              {playoffs.length > 0 && (
                <MatchList
                  title="PLAYOFFS"
                  matches={playoffs}
                />
              )}
              {league.length > 0 && (
                <MatchList
                  title="LIGA"
                  matches={league}
                />
              )}
              {playoffs.length === 0 && league.length === 0 && (
                <EmptyMatches>No hay partidas en este torneo</EmptyMatches>
              )}
            </>
          ) : errorMatches ? (
            <EmptyMatches>No hay partidas</EmptyMatches>
          ) : (
            <Loading />
          )}
        </Section>
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`
  background: var(--v-canvas);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  padding: 4rem 1.5rem;
`;

const Content = styled.div`
  width: 100%;
  max-width: 680px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1.5rem;
`;

const SectionHeader = styled.h2`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--v-fg-muted);
  margin: 0 0 1rem;
`;

const SectionBody = styled.div``;

const AvatarEditHint = styled.button`
  display: block;
  margin-top: 0.75rem;
  background: none;
  border: none;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-fg-muted);
  cursor: pointer;
  padding: 0.3rem 0;

  &:hover {
    color: var(--v-mint);
  }
`;

const EmptyState = styled.p`
  font-family: var(--v-font-body);
  font-size: 14px;
  color: var(--v-fg-muted);
  text-align: center;
  padding: 4rem 1rem;
`;

const EmptyMatches = styled.p`
  font-family: var(--v-font-body);
  font-size: 13px;
  color: var(--v-fg-muted);
  margin-top: 0.75rem;
`;

const AvatarModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
`;

const AvatarModalBody = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 2rem;
  width: 100%;
  max-width: 440px;
`;

const AvatarModalTitle = styled.h3`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-fg-muted);
  margin: 0 0 1.25rem;
  text-align: center;
`;

const AvatarInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  color: var(--v-fg);
  font-family: var(--v-font-body);
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s ease;
  box-sizing: border-box;

  &:focus {
    border-color: var(--v-mint);
  }

  &::placeholder {
    color: var(--v-fg-muted);
    opacity: 0.4;
  }
`;

const AvatarActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
  justify-content: flex-end;
`;

const AvatarCancelBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: var(--v-fg-muted);
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.6rem 1.25rem;
  border-radius: 24px;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.25);
  }
`;

const AvatarSaveBtn = styled.button`
  background: var(--v-mint);
  border: none;
  color: #000;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.6rem 1.5rem;
  border-radius: 24px;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }
`;

export default Profile;
